// CRM-14409: RAM report generation moved from one long synchronous request (which raced
// an external ~125s connection timeout) to create-token + poll. This loop is extracted out
// of the dialog component and takes its dependencies as arguments rather than importing them,
// so it can be unit-tested without a rendered component or real timers.

export const REPORT_POLL_INTERVAL_MS = 5000;
// 5s * 180 = 15 minutes - well beyond any RAM duration observed in production (up to ~6 min).
export const REPORT_POLL_MAX_ATTEMPTS = 180;

const TERMINAL_STATUSES = ['completed', 'failed', 'aborted'];

// A single poll failing (e.g. a transient network blip) shouldn't kill an otherwise-succeeding
// background job - tolerate a run of consecutive failures before giving up and rethrowing.
// 12 * REPORT_POLL_INTERVAL_MS (5s) = ~1 minute of continuous failure tolerated, well past a
// brief connectivity blip - the attempt counter above already bounds total runtime, so a longer
// error tolerance here cannot run away.
const MAX_CONSECUTIVE_POLL_ERRORS = 12;

export async function pollReportStatus({
  asyncToken,
  getStatus,
  isCancelled,
  wait
}) {
  let consecutiveErrors = 0;

  for (let attempt = 0; attempt < REPORT_POLL_MAX_ATTEMPTS; attempt++) {
    if (isCancelled()) {
      return { status: 'cancelled' };
    }

    let status;
    try {
      status = await getStatus({ asyncToken });
    } catch (err) {
      consecutiveErrors += 1;
      if (consecutiveErrors > MAX_CONSECUTIVE_POLL_ERRORS) {
        throw err;
      }

      await wait(REPORT_POLL_INTERVAL_MS);
      continue;
    }

    consecutiveErrors = 0;

    if (TERMINAL_STATUSES.includes(status.status)) {
      return status;
    }

    // generateReport() (the target of generateReportAsync()) never calls updateProgress(),
    // which is the only place AsyncActions transitions abort_queued -> aborted (see
    // TraitAsyncActions::updateProgress()/AsyncActionAbortedException). So for this flow
    // abort_queued can never become aborted on its own - treat the abort request as
    // effectively immediate from the UI's perspective, rather than polling out the full
    // REPORT_POLL_MAX_ATTEMPTS window waiting for a transition that will never happen.
    if (status.status === 'abort_queued') {
      return { ...status, status: 'aborted' };
    }

    await wait(REPORT_POLL_INTERVAL_MS);
  }

  return { status: 'timed_out' };
}

// getReportResult (unlike getStatus) is a single, unrepeated call - if it succeeds, the
// backend purges the async token as it returns, so a transient failure here would otherwise
// strand an already-completed report with no way to re-fetch it. Retry a few times before
// giving up, reusing the same interval as the status poll.
const RESULT_FETCH_MAX_ATTEMPTS = 3;

export async function fetchReportResultWithRetry({ getResult, wait }) {
  let lastError;

  for (let attempt = 0; attempt < RESULT_FETCH_MAX_ATTEMPTS; attempt++) {
    try {
      return await getResult();
    } catch (err) {
      lastError = err;
      if (attempt < RESULT_FETCH_MAX_ATTEMPTS - 1) {
        await wait(REPORT_POLL_INTERVAL_MS);
      }
    }
  }

  throw lastError;
}
